import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useContext, useMemo, useState } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';

import { useMutation, useQueryClient } from 'react-query';
import { patchAssetById } from '@services/queries/onboarding/patchAssetById';

import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import { useParams } from 'react-router-dom';
import {
  ActionButtons,
  Cancel,
  Checkbox,
  CheckContainer,
  CheckText,
  Close,
  Confirm,
  Container,
  DividerKeep,
  Header,
  HorizontalDivider,
  Input,
  InputContainer,
  Label,
  Line,
  LineContainer,
  Overlay,
  SearchedManufacturersCheckBoxes,
  SuggestionsCheckBoxWrapper,
  TextDescription,
  TextsRowFlex,
  TextsRowFlexItem,
  TextTitle,
  Title,
  TitleText,
  ToolTipText,
} from './ModelErrorDialog.styles';
import { FieldErrorTypes } from '../OnboardingFile/OnboardingFile.query';
import Labels from '../Labels/Labels';

import { UnmatchedModel } from '../UnmatchedModel/UnmatchedModel';

interface IModelErrorDialog {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
  field: FieldErrorTypes;
}

export const ModelErrorDialog = ({
  open,
  onClose,
  group,
  field,
}: IModelErrorDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const params = useParams<{ id: string }>();

  const { studioId } = useContext(NavigationContext);

  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);

  const [continueWithCurrent, setContinueWithCurrent] =
    useState<boolean>(false);
  const [continueWithSearch, setContinueWithSearch] = useState<boolean>(false);
  const [openUnmatched, setOpenUnmatched] = useState<boolean>(false);
  const [listFrom, setListFrom] = useState<'suggestion' | 'search'>(
    'suggestion',
  );

  const fieldName = useMemo(() => {
    if (field === 'model') {
      return 'Model';
    }

    if (field === 'productName') {
      return 'Product Name';
    }

    return 'Manufacturer';
  }, [group, field]);

  const fieldContent = useMemo(() => {
    if (field === 'model') {
      return group.model;
    }

    if (field === 'productName') {
      return group.productName;
    }

    return group.manufacturer;
  }, [group, field]);

  const errorMessage = useMemo(() => {
    if (field === 'manufacturer') {
      return group.mfrError;
    }

    if (field === 'model') {
      return group.mpnError;
    }

    return group.productNameError;
  }, [group, field]);

  const modelSuggestions = useMemo(() => {
    if (field === 'model') {
      return group.items[0].mpnSuggestions;
    }

    return [];
  }, [group]);

  const productSuggestions = useMemo(() => {
    if (field === 'productName') {
      return group.items[0].productNameSuggestions;
    }

    return [];
  }, [group]);

  const disabled = useMemo(() => {
    if (selected) {
      return false;
    }

    return true;
  }, [selected]);

  const patchAssetMutation = useMutation(patchAssetById, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      onClose();
    },
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>
                <BlueIcon icon="error" color="#D14343" />
                <TitleText>{fieldName} Error</TitleText>
              </Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <TextsRowFlex>
              {fieldContent ? (
                <TextsRowFlexItem>
                  <TextTitle>Original Inputs</TextTitle>
                  <TextDescription>{fieldContent}</TextDescription>
                </TextsRowFlexItem>
              ) : (
                <div />
              )}

              <TextsRowFlexItem>
                {errorMessage && (
                  <Labels
                    type="error"
                    text={errorMessage}
                    toolTipId="manufacturerTooltipId"
                    toolTipContent={
                      <>
                        <ToolTipText>
                          {fieldName} is either incorrect or does not exist.
                        </ToolTipText>
                      </>
                    }
                  />
                )}
              </TextsRowFlexItem>
            </TextsRowFlex>

            <DividerKeep />

            <SuggestionsCheckBoxWrapper>
              <CheckContainer
                onClick={() => {
                  setSelected(fieldContent);
                  setContinueWithCurrent(true);
                  setContinueWithSearch(false);
                }}
              >
                <Checkbox
                  active={selected === fieldContent && continueWithCurrent}
                />
                <CheckText>{`Continue with "${fieldContent}"`}</CheckText>
              </CheckContainer>
            </SuggestionsCheckBoxWrapper>

            <HorizontalDivider />

            {!!modelSuggestions.length && (
              <>
                <Label>
                  {fieldName} Number suggestions{' '}
                  <span>(based on Original Input)</span>
                </Label>

                <SuggestionsCheckBoxWrapper>
                  {modelSuggestions.map(suggestion => (
                    <CheckContainer
                      key={suggestion.product_model}
                      onClick={() => {
                        setSelected(suggestion.product_model);
                        setContinueWithCurrent(false);
                        setContinueWithSearch(false);
                        setListFrom('suggestion');
                      }}
                    >
                      <Checkbox
                        active={
                          selected === suggestion.product_model &&
                          listFrom === 'suggestion' &&
                          !continueWithSearch &&
                          !continueWithCurrent
                        }
                      />
                      <CheckText>{suggestion.product_model}</CheckText>
                    </CheckContainer>
                  ))}
                </SuggestionsCheckBoxWrapper>

                <HorizontalDivider />
              </>
            )}

            {!!productSuggestions.length && (
              <>
                <Label>
                  {fieldName} Name suggestions{' '}
                  <span>(based on Original Input)</span>
                </Label>

                <SuggestionsCheckBoxWrapper>
                  {productSuggestions.map(suggestion => (
                    <CheckContainer
                      key={suggestion.product_name}
                      onClick={() => {
                        setSelected(suggestion.product_name);
                        setContinueWithCurrent(false);
                        setContinueWithSearch(false);
                        setListFrom('suggestion');
                      }}
                    >
                      <Checkbox
                        active={
                          selected === suggestion.product_name &&
                          listFrom === 'suggestion' &&
                          !continueWithSearch &&
                          !continueWithCurrent
                        }
                      />
                      <CheckText>{suggestion.product_name}</CheckText>
                    </CheckContainer>
                  ))}
                </SuggestionsCheckBoxWrapper>

                <HorizontalDivider />
              </>
            )}

            <Label>Type a new {fieldName}</Label>

            <InputContainer>
              <Input
                placeholder={`New ${fieldName}`}
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  if (continueWithSearch) {
                    setSelected(e.target.value);
                  }

                  if (e.target.value === '') {
                    if (continueWithSearch) {
                      setSelected(null);
                    }
                    setContinueWithSearch(false);
                  }
                }}
              />
              {!!search.length && (
                <BlueIcon
                  icon="small-cross"
                  color="#8F95B2"
                  onClick={() => {
                    setSearch('');
                    setContinueWithSearch(false);
                    if (continueWithSearch) {
                      setSelected(null);
                    }
                  }}
                />
              )}
            </InputContainer>

            <SearchedManufacturersCheckBoxes>
              {search && (
                <CheckContainer
                  onClick={() => {
                    setSelected(search);
                    setContinueWithSearch(true);
                    setContinueWithCurrent(false);
                  }}
                >
                  <Checkbox
                    active={selected === search && continueWithSearch}
                  />
                  <CheckText>{`Continue with "${search}"`}</CheckText>
                </CheckContainer>
              )}
            </SearchedManufacturersCheckBoxes>

            <LineContainer>
              <Line>
                <ActionButtons>
                  <Cancel type="button" onClick={onClose}>
                    Cancel
                  </Cancel>
                  <Confirm
                    disabled={disabled || patchAssetMutation.isLoading}
                    onClick={() => {
                      if (selected) {
                        if (continueWithCurrent) {
                          setOpenUnmatched(true);
                        } else if (fieldName === 'Model') {
                          patchAssetMutation.mutate({
                            groupId: group.id,
                            studioId,
                            mpn: selected,
                            mpnError: null,
                            fileId: params.id,
                          });
                        } else {
                          patchAssetMutation.mutate({
                            groupId: group.id,
                            studioId,
                            productName: selected,
                            productNameError: null,
                            fileId: params.id,
                          });
                        }
                      }
                    }}
                  >
                    Confirm
                  </Confirm>
                </ActionButtons>
              </Line>
            </LineContainer>

            {openUnmatched && selected && (
              <UnmatchedModel
                open={openUnmatched}
                onClose={() => setOpenUnmatched(false)}
                group={group}
                field={field}
                closeFlow={onClose}
              />
            )}
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
